import { useEffect, useRef, useState } from 'react';

const SAVE_DELAY_MS = 700;

/**
 * Salvamento a cada alteração, no mesmo molde do mapa corporal: espera um
 * instante, grava só o último valor, regrava se algo mudou durante o envio e
 * grava o pendente ao sair da tela. `save(value, done)` chama a API e
 * `done(ok)` ao terminar. O valor pode ser null (limpar).
 */
export default function useScaleAutosave(save) {
  const [saveState, setSaveState] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);

  // { value } enquanto há alteração esperando; o embrulho deixa o null ser um valor válido
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const flushRef = useRef(null);

  function flush() {
    const pending = pendingRef.current;
    if (!pending || busyRef.current) return;
    pendingRef.current = null;
    busyRef.current = true;
    setSaveState('saving');
    save(pending.value, (ok) => {
      busyRef.current = false;
      if (ok) {
        setSaveState('saved');
        setSavedAt(new Date());
      } else {
        setSaveState('error');
      }
      if (pendingRef.current) flush();
    });
  }
  flushRef.current = flush;

  useEffect(() => {
    const timer = timerRef;
    const pendingFlush = flushRef;
    return () => {
      clearTimeout(timer.current);
      if (pendingFlush.current) pendingFlush.current();
    };
  }, []);

  function queue(value) {
    pendingRef.current = { value };
    setSaveState('pending');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flushRef.current(), SAVE_DELAY_MS);
  }

  const isDirty = () => Boolean(pendingRef.current) || busyRef.current;

  return { saveState, savedAt, queue, isDirty };
}
